import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Download, ShieldCheck } from "lucide-react";

const ACCION_LABELS = {
  crear: "Creó", modificar: "Modificó", desactivar: "Desactivó", invalidar: "Invalidó",
  bloquear: "Bloqueó", desbloquear: "Desbloqueó", login: "Inició sesión",
  aprobar_fuera_tarifa: "Aprobó fuera de tarifa", procesar_ia: "Procesó con IA",
  confirmar_extraccion_ia: "Confirmó extracción IA", duplicado_consciente: "Creó duplicado a propósito",
};
const ENTIDADES = ["load", "load_cost", "client", "carrier", "user", "company", "settings", "document", "qbo_invoice"];

export default function Auditoria() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [usuarios, setUsuarios] = useState([]);
  const [userId, setUserId] = useState("");
  const [accion, setAccion] = useState("");
  const [entidad, setEntidad] = useState("");
  const [search, setSearch] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [soloSensibles, setSoloSensibles] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/audit-log/usuarios").then(({ data }) => setUsuarios(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (userId) params.user_id = userId;
      if (accion) params.accion = accion;
      if (entidad) params.entidad = entidad;
      if (search) params.search = search;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (soloSensibles) params.solo_sensibles = "true";
      const { data: res } = await api.get("/audit-log", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [userId, accion, entidad, search, fechaDesde, fechaHasta, soloSensibles]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportar = (fmt) => {
    const params = new URLSearchParams();
    if (userId) params.set("user_id", userId);
    if (accion) params.set("accion", accion);
    if (entidad) params.set("entidad", entidad);
    if (search) params.set("search", search);
    if (fechaDesde) params.set("fecha_desde", fechaDesde);
    if (fechaHasta) params.set("fecha_hasta", fechaHasta);
    if (soloSensibles) params.set("solo_sensibles", "true");
    window.open(`${api.defaults.baseURL}/audit-log/export/${fmt}?${params.toString()}`, "_blank");
  };

  return (
    <div>
      <PageHeader
        title="Auditoría"
        subtitle="Registro completo de actividad del sistema — de solo lectura, sin excepciones (solo Dueño)"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportar("csv")} className="gap-2" data-testid="audit-export-csv">
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" onClick={() => exportar("xlsx")} className="gap-2" data-testid="audit-export-xlsx">
              <Download className="h-4 w-4" /> Excel
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input placeholder="Buscar (descripción, campo, valor, usuario)..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="audit-search-input" />
        <Select value={userId || "todos"} onValueChange={(v) => setUserId(v === "todos" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="audit-filter-usuario"><SelectValue placeholder="Usuario" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los usuarios</SelectItem>
            {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre_completo}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entidad || "todas"} onValueChange={(v) => setEntidad(v === "todas" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="audit-filter-entidad"><SelectValue placeholder="Entidad" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las entidades</SelectItem>
            {ENTIDADES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={accion || "todas"} onValueChange={(v) => setAccion(v === "todas" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="audit-filter-accion"><SelectValue placeholder="Acción" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las acciones</SelectItem>
            {Object.entries(ACCION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="w-40" data-testid="audit-fecha-desde" />
        <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="w-40" data-testid="audit-fecha-hasta" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={soloSensibles} onChange={(e) => setSoloSensibles(e.target.checked)} data-testid="audit-filter-sensibles" />
          Solo eventos sensibles
        </label>
      </div>

      <div className="rounded-md border border-border bg-white" data-testid="audit-table-container">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead>Acción</TableHead>
              <TableHead>Entidad</TableHead>
              <TableHead>Campo</TableHead>
              <TableHead>Valor anterior</TableHead>
              <TableHead>Valor nuevo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground" data-testid="audit-empty-state">
                No hay registros con estos filtros.
              </TableCell></TableRow>
            )}
            {data.items.map((a, i) => {
              const esMonto = a.campo_modificado && /monto|cobro|pago|margen/i.test(a.campo_modificado);
              return (
                <TableRow key={a.id} data-testid={`audit-row-${i}`} className={a.sensible ? "bg-red-50/50" : ""}>
                  <TableCell className="text-xs text-muted-foreground">{a.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell className="text-sm">{a.user_nombre}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1.5">
                      {a.sensible && <AlertTriangle className="h-3.5 w-3.5 text-red-600" data-testid={`audit-sensible-${i}`} />}
                      {ACCION_LABELS[a.accion] || a.accion}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="font-mono text-xs">{a.entidad}</span>
                    <div className="text-xs text-muted-foreground">{a.entidad_descripcion}</div>
                  </TableCell>
                  <TableCell className="text-xs">{a.campo_modificado || "—"}</TableCell>
                  <TableCell className={`text-xs ${esMonto ? "font-semibold text-red-600" : "text-muted-foreground"}`}>{a.valor_anterior ?? "—"}</TableCell>
                  <TableCell className={`text-xs ${esMonto ? "font-semibold text-green-700" : "text-muted-foreground"}`}>{a.valor_nuevo ?? "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> Este registro es de solo lectura absoluta: no existe endpoint para editar ni borrar entradas.
      </p>
    </div>
  );
}
